import { useRef, useState } from "react";
import { Badge, Button, Field, Select, TextInput } from "@odingard/design-system";
import type { ExtractionResult, StudioDocument } from "@odingard/sdk-client";
import { SUPPORTED_DOCUMENT_MIME_TYPES, toBase64 } from "@odingard/sdk-client";
import { documentsApi } from "../lib/api";

// Shared Studio Documents surface (ODS-DOCUMENTS-001 P4).
//
// One implementation for every product: a document is uploaded to the shared
// Documents Platform and extracted through its canonical contract, so Contract
// AI, Policy AI and Proposal AI consume the same artifact instead of each
// growing an uploader. Nothing here holds an object-store credential — the
// bytes go to the Studio Worker, which authorizes the write as the acting member
// against the platform.

/** Reads the exact bytes a user selected, so the platform hashes that file. */
export function readBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The file could not be read."));
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.readAsArrayBuffer(file);
  });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Media types the browser offers. OCR is not a Documents capability yet. */
export const DOCUMENT_ACCEPT = ".txt,.md,.pdf,.docx";

export function mimeTypeFor(file: File): string | null {
  if (SUPPORTED_DOCUMENT_MIME_TYPES.includes(file.type)) return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith(".txt")) return "text/plain";
  if (name.endsWith(".md")) return "text/markdown";
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".docx")) {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  return null;
}

export function extractionTone(result: ExtractionResult): "success" | "warning" {
  return result.status === "extracted" ? "success" : "warning";
}

/** Plain English for a governed extraction outcome, including OCR refusals. */
export function extractionSummary(result: ExtractionResult): string {
  if (result.status === "extracted") {
    const pages = result.pageCount === null ? "" : ` · ${result.pageCount} page(s)`;
    return `Extracted ${result.characterCount} characters${pages} with ${result.extractor} ${result.extractorVersion}`;
  }
  if (result.failureCode === "OCR_REQUIRED") {
    return "This document needs OCR, which Studio Documents does not do yet. No text was read, so it cannot back evidence.";
  }
  return result.failureReason ?? "Studio Documents could not read this document.";
}

export interface UploadOutcome {
  document: StudioDocument;
  extraction: ExtractionResult | null;
  /** Set when extraction itself could not be attempted. */
  error: string | null;
}

/**
 * Upload control. Extraction is run immediately after the upload so the outcome
 * a user sees is the platform's own governed result, not an optimistic guess.
 */
export function DocumentUpload({
  orgId,
  onUploaded,
  label = "Upload a document",
}: {
  orgId: string;
  onUploaded?: (outcome: UploadOutcome) => void;
  label?: string;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<UploadOutcome | null>(null);

  const reset = () => {
    setFile(null);
    setDisplayName("");
    if (input.current) input.current.value = "";
  };

  const submit = async () => {
    if (!file) return;
    const mimeType = mimeTypeFor(file);
    if (!mimeType) {
      setError(
        "Studio Documents reads text, machine-readable PDF and DOCX today. Scans and images need OCR, which is not available yet.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    setOutcome(null);
    try {
      const bytes = await readBytes(file);
      const { document } = await documentsApi.upload(orgId, {
        filename: file.name,
        mimeType,
        contentBase64: toBase64(bytes),
        declaredSize: bytes.byteLength,
        // Stable per file and size, so a double click imports one document.
        idempotencyKey: `web-${file.name}-${bytes.byteLength}-${file.lastModified}`,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      });
      let extraction: ExtractionResult | null = null;
      let extractionError: string | null = null;
      try {
        extraction = await documentsApi.extract(orgId, document.id);
      } catch (err) {
        extractionError = err instanceof Error ? err.message : "Extraction failed.";
      }
      const next: UploadOutcome = { document, extraction, error: extractionError };
      setOutcome(next);
      reset();
      onUploaded?.(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ods-stack">
      <Field
        label={label}
        help="Text, machine-readable PDF and DOCX. The file is stored by the shared Documents Platform, not by this page."
        {...(error ? { error } : {})}
      >
        {(id) => (
          <input
            id={id}
            ref={input}
            type="file"
            accept={DOCUMENT_ACCEPT}
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setError(null);
            }}
          />
        )}
      </Field>
      <Field label="Display name (optional)">
        {(id) => (
          <TextInput
            id={id}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Access Control Policy"
          />
        )}
      </Field>
      <div className="badge-row">
        <Button onClick={submit} disabled={!file || busy}>
          {busy ? "Uploading…" : "Upload and extract"}
        </Button>
        {file ? <span className="muted-note">{formatBytes(file.size)}</span> : null}
      </div>
      {outcome ? <UploadResult outcome={outcome} /> : null}
    </div>
  );
}

function UploadResult({ outcome }: { outcome: UploadOutcome }) {
  const { document, extraction, error } = outcome;
  return (
    <div className="ods-stack" data-testid="document-upload-result">
      <div className="badge-row">
        <Badge tone="info">Stored</Badge>
        <strong>{document.displayName || document.filename}</strong>
        <span className="muted-note">{formatBytes(document.size)}</span>
      </div>
      <dl className="detail-grid">
        <div>
          <dt>Document checksum</dt>
          <dd>
            <code>{document.checksum}</code>
          </dd>
        </div>
        {extraction ? (
          <>
            <div>
              <dt>Extraction</dt>
              <dd>
                <Badge tone={extractionTone(extraction)}>
                  {extraction.status === "extracted" ? "Extracted" : (extraction.failureCode ?? "Unsupported")}
                </Badge>{" "}
                {extractionSummary(extraction)}
              </dd>
            </div>
            {extraction.extractionChecksum ? (
              <div>
                <dt>Extraction checksum</dt>
                <dd>
                  <code>{extraction.extractionChecksum}</code>
                </dd>
              </div>
            ) : null}
          </>
        ) : null}
      </dl>
      {error ? <p className="muted-note">Extraction could not be run: {error}</p> : null}
    </div>
  );
}

/**
 * Reusable governed picker over documents the shared platform holds. Governed
 * surfaces select from this list rather than uploading their own copy.
 */
export function DocumentSelect({
  documents,
  value,
  onChange,
  label = "Studio Documents record",
}: {
  documents: StudioDocument[];
  value: string;
  onChange: (documentId: string) => void;
  label?: string;
}) {
  return (
    <Field label={label} help="Documents the shared platform already stores for this organization.">
      {(id) => (
        <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select a document</option>
          {documents.map((document) => (
            <option key={document.id} value={document.id}>
              {document.displayName || document.filename} · {formatBytes(document.size)}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}
